import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import StickyActions from "./components/StickyActions";
import BookTestFAB from "./components/BookTestFAB";
import InstallAppPrompt from "./components/InstallAppPrompt";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider } from "./context/AuthContext";

// Lazy load routes for performance
const Home = lazy(() => import("./pages/Home"));
const Tests = lazy(() => import("./pages/Tests"));
const TestDetail = lazy(() => import("./pages/TestDetail"));
const Packages = lazy(() => import("./pages/Packages"));
const PackageDetail = lazy(() => import("./pages/PackageDetail"));
const LocationsPage = lazy(() => import("./pages/Locations").then(m => ({ default: m.Locations })));
const LocationDetail = lazy(() => import("./pages/Locations").then(m => ({ default: m.LocationDetail })));
const HomeCollection = lazy(() => import("./pages/HomeCollection"));
const ReportDownload = lazy(() => import("./pages/ReportDownload"));
const Blog = lazy(() => import("./pages/Blog").then(m => ({ default: m.Blog })));
const BlogDetail = lazy(() => import("./pages/Blog").then(m => ({ default: m.BlogDetail })));
const About = lazy(() => import("./pages/AboutContact").then(m => ({ default: m.About })));
const Contact = lazy(() => import("./pages/AboutContact").then(m => ({ default: m.Contact })));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Account = lazy(() => import("./pages/Account"));
const Consult = lazy(() => import("./pages/Consult"));
const FAQ = lazy(() => import("./pages/FAQ"));
const BookTest = lazy(() => import("./pages/BookTest"));
const PillarPage = lazy(() => import("./pages/seo/PillarPage"));
const TestSeoPage = lazy(() => import("./pages/seo/TestSeoPage"));
const PackageSeoPage = lazy(() => import("./pages/seo/PackageSeoPage"));
const LocalitySeoPage = lazy(() => import("./pages/seo/LocalitySeoPage"));

// Admin
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminBrand = lazy(() => import("./pages/admin/AdminBrand"));
const AdminTests = lazy(() => import("./pages/admin/AdminTests"));
const AdminPackages = lazy(() => import("./pages/admin/AdminPackages"));
const AdminBlogs = lazy(() => import("./pages/admin/AdminBlogs"));
const AdminLocations = lazy(() => import("./pages/admin/AdminLocations"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminBookings = lazy(() => import("./pages/admin/AdminBookings"));
const AdminContacts = lazy(() => import("./pages/admin/AdminContacts"));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const Loading = () => (
  <div className="min-h-[60vh] grid place-items-center text-slate-400 text-sm">Loading…</div>
);

const Shell = ({ children }) => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const isAuthCb = pathname.startsWith("/auth/");
  const bare = isAdmin || isAuthCb;
  return (
    <>
      {!bare && <Header />}
      <main className={bare ? "" : "pb-20 md:pb-0"}>
        <Suspense fallback={<Loading />}>{children}</Suspense>
      </main>
      {!bare && <Footer />}
      {!bare && <StickyActions />}
      {!bare && <BookTestFAB />}
      {!bare && <InstallAppPrompt />}
    </>
  );
};

function App() {
  return (
    <div className="App bg-white text-slate-900">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Shell>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/tests" element={<Tests />} />
              <Route path="/tests/:slug" element={<TestSeoPage />} />
              <Route path="/tests/detail/:id" element={<TestDetail />} />
              <Route path="/packages" element={<Packages />} />
              <Route path="/packages/:id" element={<PackageDetail />} />
              <Route path="/health-packages" element={<Packages />} />
              <Route path="/health-packages/:slug" element={<PackageSeoPage />} />
              <Route path="/locations" element={<LocationsPage />} />
              <Route path="/locations/:slug" element={<LocalitySeoPage />} />
              <Route path="/locations/branch/:id" element={<LocationDetail />} />
              <Route path="/home-collection" element={<HomeCollection />} />
              <Route path="/home-sample-collection-ranchi" element={<PillarPage slugOverride="home-sample-collection-ranchi" />} />
              <Route path="/pathology-lab-ranchi" element={<PillarPage slugOverride="pathology-lab-ranchi" />} />
              <Route path="/diagnostic-centre-ranchi" element={<PillarPage slugOverride="diagnostic-centre-ranchi" />} />
              <Route path="/blood-test-ranchi" element={<PillarPage slugOverride="blood-test-ranchi" />} />
              <Route path="/health-checkup-ranchi" element={<PillarPage slugOverride="health-checkup-ranchi" />} />
              <Route path="/book-test" element={<BookTest />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/report" element={<ReportDownload />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:id" element={<BlogDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/about-doctorlab" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/account" element={<Account />} />
              <Route path="/consult" element={<Consult />} />
              <Route path="/auth/callback" element={<AuthCallback />} />

              {/* Admin */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="brand" element={<AdminBrand />} />
                <Route path="tests" element={<AdminTests />} />
                <Route path="packages" element={<AdminPackages />} />
                <Route path="blogs" element={<AdminBlogs />} />
                <Route path="locations" element={<AdminLocations />} />
                <Route path="reports" element={<AdminReports />} />
                <Route path="bookings" element={<AdminBookings />} />
                <Route path="contacts" element={<AdminContacts />} />
              </Route>
            </Routes>
          </Shell>
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
